import { useState, useCallback, useRef } from 'react';
import { api, type PolicyPack } from '../api/client';

export interface ChatMessage {
  role: 'user' | 'assistant';
  content: string;
}

export interface UsePolicyChatReturn {
  messages: ChatMessage[];
  currentPolicy: PolicyPack | null;
  isStreaming: boolean;
  error: string | null;
  sendMessage: (text: string) => void;
  setCurrentPolicy: (policy: PolicyPack | null) => void;
  clearChat: () => void;
}

export function usePolicyChat(workspaceId: string | undefined): UsePolicyChatReturn {
  const [messages, setMessages] = useState<ChatMessage[]>([]);
  const [currentPolicy, setCurrentPolicy] = useState<PolicyPack | null>(null);
  const [isStreaming, setIsStreaming] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const abortRef = useRef<AbortController | null>(null);

  const sendMessage = useCallback(
    async (text: string) => {
      if (!workspaceId || !text.trim() || isStreaming) return;

      const userMsg: ChatMessage = { role: 'user', content: text.trim() };
      const updatedMessages = [...messages, userMsg];
      setMessages(updatedMessages);
      setIsStreaming(true);
      setError(null);

      const controller = new AbortController();
      abortRef.current = controller;

      try {
        const response = await api.policyChatStream(
          workspaceId,
          updatedMessages.map((m) => ({ role: m.role, content: m.content })),
          currentPolicy ?? undefined,
        );

        if (!response.ok) {
          const body = await response.json().catch(() => ({}));
          const errMsg = body.error || `Request failed: ${response.status}`;
          if (response.status === 429) {
            setError('Rate limit reached. Please wait a moment before trying again.');
          } else {
            setError(errMsg);
          }
          setIsStreaming(false);
          return;
        }

        if (!response.body) {
          setError('No response body');
          setIsStreaming(false);
          return;
        }

        const reader = response.body.getReader();
        const decoder = new TextDecoder();
        let assistantContent = '';
        let buffer = '';

        while (true) {
          const { done, value } = await reader.read();
          if (done) break;

          buffer += decoder.decode(value, { stream: true });
          const lines = buffer.split('\n');
          buffer = lines.pop() || '';

          let currentEvent = '';

          for (const line of lines) {
            if (line.startsWith('event: ')) {
              currentEvent = line.slice(7).trim();
            } else if (line.startsWith('data: ')) {
              const data = line.slice(6);

              if (currentEvent === 'text') {
                try {
                  assistantContent += JSON.parse(data);
                } catch {
                  assistantContent += data;
                }
                setMessages([...updatedMessages, { role: 'assistant', content: assistantContent }]);
              } else if (currentEvent === 'policy') {
                try {
                  const parsed = JSON.parse(data) as PolicyPack;
                  setCurrentPolicy(parsed);
                } catch {
                  // ignore parse errors
                }
              } else if (currentEvent === 'error') {
                setError(data);
              } else if (currentEvent === 'done') {
                // stream complete
              }
            }
          }
        }

        if (assistantContent) {
          setMessages([...updatedMessages, { role: 'assistant', content: assistantContent }]);
        }
      } catch (err) {
        if ((err as Error).name !== 'AbortError') {
          setError((err as Error).message);
        }
      } finally {
        setIsStreaming(false);
        abortRef.current = null;
      }
    },
    [workspaceId, messages, currentPolicy, isStreaming],
  );

  const clearChat = useCallback(() => {
    setMessages([]);
    setError(null);
  }, []);

  return { messages, currentPolicy, isStreaming, error, sendMessage, setCurrentPolicy, clearChat };
}
